import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import {
  Plus,
  Edit,
  Loader2,
  Package,
  DollarSign,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${baseUrl}${url}`;
};

const getVariantImageSrc = (image) => {
  if (image?.urls?.small) return image.urls.small;
  if (image?.urls?.medium) return image.urls.medium;
  if (image?.urls?.original) return image.urls.original;
  return resolveImageUrl(image?.url || '');
};

const getInventoryHealthMeta = (status) => {
  const value = (status || 'normal').toLowerCase();
  if (value === 'fast_moving') {
    return { label: 'Fast Moving', className: 'bg-emerald-100 text-emerald-800' };
  }
  if (value === 'slow_moving') {
    return { label: 'Slow Moving', className: 'bg-amber-100 text-amber-800' };
  }
  if (value === 'dead_stock_risk') {
    return { label: 'Dead Stock Risk', className: 'bg-rose-100 text-rose-800' };
  }
  return { label: 'Normal', className: 'bg-slate-100 text-slate-700' };
};

const LabelWithTooltip = ({ text, tooltip }) => (
  <div className="flex items-center gap-2">
    <Label className="text-[#5C4033]">{text}</Label>
    {tooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-[#A8A8A8] hover:text-[#5C4033] transition-colors"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    ) : null}
  </div>
);

export default function ProductsPage() {
  const navigate = useNavigate();
  const [masters, setMasters] = useState([]);
  const [packSizes, setPackSizes] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('variants');

  const [showMasterModal, setShowMasterModal] = useState(false);
  const [showPackModal, setShowPackModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);

  const [saving, setSaving] = useState(false);

  const [selectedMaster, setSelectedMaster] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantImages, setVariantImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [stickerUploading, setStickerUploading] = useState(false);

  const [masterForm, setMasterForm] = useState({
    name: '',
    description: '',
    category: 'spices',
    code: '',
    hsn_code: '',
    is_active: true
  });

  const [packForm, setPackForm] = useState({
    label: '',
    grams: '',
    size_code: '',
    is_active: true
  });

  const [variantForm, setVariantForm] = useState({
    master_id: '',
    pack_size_id: '',
    sku: '',
    invoice_name: '',
    is_blended: false,
    is_bulk: false,
    reorder_level: '',
    is_active: true
  });

  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingTab, setPricingTab] = useState('costing');
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [selectedPricingVariant, setSelectedPricingVariant] = useState(null);
  const [pricingData, setPricingData] = useState({
    costing: null,
    pricing: null,
    rules: null,
    discounts: [],
    history: []
  });
  const [costingForm, setCostingForm] = useState({
    raw_material_cost_per_kg: '',
    processing_cost_per_kg: '',
    packaging_cost_per_kg: '',
    overhead_cost_per_kg: '',
    wastage_percentage: '',
    last_reviewed_on: '',
    reason: ''
  });
  const [pricingForm, setPricingForm] = useState({
    retail_mrp: '',
    retail_price: '',
    wholesale_price: '',
    pricing_basis: 'COST_BASED',
    active: true,
    reason: ''
  });
  const [rulesForm, setRulesForm] = useState({
    minimum_margin_percentage: '',
    max_discount_end_customer: '',
    max_discount_end_customer_type: 'FLAT',
    max_discount_retail_shop: '',
    max_discount_retail_shop_type: 'FLAT',
    max_discount_wholesale: '',
    max_discount_wholesale_type: 'FLAT',
    warning_threshold_percentage: '0',
    hard_block: true,
    allow_below_minimum: false,
    no_discount: false,
    reason: ''
  });
  const [discountSlabs, setDiscountSlabs] = useState([]);
  const [discountReason, setDiscountReason] = useState('');

  const buyerTypes = [
    { value: 'END_CUSTOMER', label: 'End Customer' },
    { value: 'RETAIL_SHOP', label: 'Retail Shop' },
    { value: 'WHOLESALE', label: 'Wholesale Customer' }
  ];

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const computeCostingTotals = () => {
    const packGrams = selectedPricingVariant?.pack_size_grams || 0;
    const subtotal =
      parseNumber(costingForm.raw_material_cost_per_kg) +
      parseNumber(costingForm.processing_cost_per_kg) +
      parseNumber(costingForm.packaging_cost_per_kg) +
      parseNumber(costingForm.overhead_cost_per_kg);
    const wastageMultiplier = 1 + parseNumber(costingForm.wastage_percentage) / 100;
    const totalCostPerKg = subtotal * wastageMultiplier;
    const totalCostPerPack = totalCostPerKg * (packGrams / 1000);
    return {
      totalCostPerKg: Number.isFinite(totalCostPerKg) ? totalCostPerKg : 0,
      totalCostPerPack: Number.isFinite(totalCostPerPack) ? totalCostPerPack : 0
    };
  };

  const computeMinimumSellingPrice = () => {
    const totals = computeCostingTotals();
    const minMargin = parseNumber(rulesForm.minimum_margin_percentage);
    const minPricePerKg = totals.totalCostPerKg * (1 + minMargin / 100);
    const minPricePerPack = totals.totalCostPerPack * (1 + minMargin / 100);
    return {
      minPricePerKg: Number.isFinite(minPricePerKg) ? minPricePerKg : 0,
      minPricePerPack: Number.isFinite(minPricePerPack) ? minPricePerPack : 0
    };
  };

  const getPricingErrors = () => {
    const errors = [];
    const totals = computeCostingTotals();
    if (!totals.totalCostPerPack || !parseNumber(rulesForm.minimum_margin_percentage)) {
      return errors;
    }
    const roundToTwo = (value) => Math.round((value || 0) * 100) / 100;
    const minMargin = parseNumber(rulesForm.minimum_margin_percentage);
    const minPricePerPack = roundToTwo(totals.totalCostPerPack * (1 + minMargin / 100));
    const entries = [
      { label: 'End Customer (MRP)', value: parseNumber(pricingForm.retail_mrp) },
      { label: 'Retail Shop', value: parseNumber(pricingForm.retail_price) },
      { label: 'Wholesale', value: parseNumber(pricingForm.wholesale_price) }
    ];
    entries.forEach((entry) => {
      const priceRounded = roundToTwo(entry.value);
      if (priceRounded && priceRounded < minPricePerPack) {
        errors.push(`${entry.label} price below minimum selling price.`);
      }
      if (entry.value && totals.totalCostPerPack) {
        const margin = ((entry.value - totals.totalCostPerPack) / totals.totalCostPerPack) * 100;
        const marginRounded = roundToTwo(margin);
        if (marginRounded < minMargin) {
          errors.push(`${entry.label} margin below minimum.`);
        }
      }
    });
    if (parseNumber(pricingForm.retail_price) >= parseNumber(pricingForm.retail_mrp)) {
      errors.push('Retail price must be lower than Retail MRP.');
    }
    if (parseNumber(pricingForm.wholesale_price) >= parseNumber(pricingForm.retail_price)) {
      errors.push('Wholesale price must be lower than Retail price.');
    }
    return errors;
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mastersRes, packRes, variantsRes] = await Promise.all([
        adminApi.getProductMasters(),
        adminApi.getPackSizes(),
        adminApi.getProductVariants(),
      ]);
      setMasters(mastersRes.data);
      setPackSizes(packRes.data);
      setVariants(variantsRes.data);
    } catch (error) {
      toast.error('Failed to load product data');
    } finally {
      setLoading(false);
    }
  };

  const masterOptions = useMemo(
    () =>
      masters
        .filter((m) => m.is_active)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' })),
    [masters]
  );
  const packOptions = useMemo(() => packSizes.filter((p) => p.is_active), [packSizes]);
  const variantHealthStats = useMemo(() => {
    return variants.reduce((acc, item) => {
      const health = (item.inventory_health || 'normal').toLowerCase();
      if (health === 'fast_moving') acc.fast += 1;
      else if (health === 'slow_moving') acc.slow += 1;
      else if (health === 'dead_stock_risk') acc.dead += 1;
      else acc.normal += 1;
      return acc;
    }, { fast: 0, slow: 0, dead: 0, normal: 0 });
  }, [variants]);

  const selectedVariantMaster = useMemo(
    () => masterOptions.find((m) => m.id === variantForm.master_id),
    [masterOptions, variantForm.master_id]
  );
  const selectedVariantPack = useMemo(
    () => packOptions.find((p) => p.id === variantForm.pack_size_id),
    [packOptions, variantForm.pack_size_id]
  );

  const invoiceNamePreview = useMemo(() => {
    const masterName = selectedVariantMaster?.name || '';
    if (!masterName) return '';
    if (variantForm.is_bulk) return `${masterName} 1kg`;
    const packLabel = selectedVariantPack?.label || '';
    return packLabel ? `${masterName} ${packLabel}` : masterName;
  }, [selectedVariantMaster, selectedVariantPack, variantForm.is_bulk]);

  const resetMasterForm = () => {
    setMasterForm({
      name: '',
      description: '',
      category: 'spices',
      code: '',
      hsn_code: '',
      is_active: true
    });
    setSelectedMaster(null);
  };

  const resetPackForm = () => {
    setPackForm({
      label: '',
      grams: '',
      size_code: '',
      is_active: true
    });
    setSelectedPack(null);
  };

  const resetVariantForm = () => {
    setVariantForm({
      master_id: '',
      pack_size_id: '',
      sku: '',
      invoice_name: '',
      is_blended: false,
      is_bulk: false,
      reorder_level: '',
      is_active: true
    });
    setSelectedVariant(null);
    setVariantImages([]);
  };

  const resetPricingForms = () => {
    setPricingData({
      costing: null,
      pricing: null,
      rules: null,
      discounts: [],
      history: []
    });
    setCostingForm({
      raw_material_cost_per_kg: '',
      processing_cost_per_kg: '',
      packaging_cost_per_kg: '',
      overhead_cost_per_kg: '',
      wastage_percentage: '',
      last_reviewed_on: '',
      reason: ''
    });
    setPricingForm({
      retail_mrp: '',
      retail_price: '',
      wholesale_price: '',
      pricing_basis: 'COST_BASED',
      active: true,
      reason: ''
    });
    setRulesForm({
      minimum_margin_percentage: '',
      max_discount_end_customer: '',
      max_discount_end_customer_type: 'FLAT',
      max_discount_retail_shop: '',
      max_discount_retail_shop_type: 'FLAT',
      max_discount_wholesale: '',
      max_discount_wholesale_type: 'FLAT',
      warning_threshold_percentage: '0',
      hard_block: true,
      allow_below_minimum: false,
      no_discount: false,
      reason: ''
    });
    setDiscountSlabs([]);
    setDiscountReason('');
  };

  const loadPricingModule = async (variant) => {
    setPricingLoading(true);
    try {
      const response = await adminApi.getVariantPricingModule(variant.id);
      const data = response.data || {};
      setPricingData({
        costing: data.costing || null,
        pricing: data.pricing || null,
        rules: data.rules || null,
        discounts: data.discounts || [],
        history: data.history || []
      });

      const costing = data.costing || {};
      setCostingForm({
        raw_material_cost_per_kg: costing.raw_material_cost_per_kg?.toString() || '',
        processing_cost_per_kg: costing.processing_cost_per_kg?.toString() || '',
        packaging_cost_per_kg: costing.packaging_cost_per_kg?.toString() || '',
        overhead_cost_per_kg: costing.overhead_cost_per_kg?.toString() || '',
        wastage_percentage: costing.wastage_percentage?.toString() || '',
        last_reviewed_on: formatDateInput(costing.last_reviewed_on),
        reason: ''
      });

      const pricing = data.pricing || {};
      setPricingForm({
        retail_mrp: pricing.retail_mrp?.toString() || '',
        retail_price: pricing.retail_price?.toString() || '',
        wholesale_price: pricing.wholesale_price?.toString() || '',
        pricing_basis: pricing.pricing_basis || 'COST_BASED',
        active: pricing.active ?? true,
        reason: ''
      });

      const rules = data.rules || {};
      const noDiscountDetected =
        parseNumber(rules.max_discount_end_customer) === 0 &&
        parseNumber(rules.max_discount_retail_shop) === 0 &&
        parseNumber(rules.max_discount_wholesale) === 0 &&
        (rules.max_discount_end_customer_type || 'PERCENTAGE') === 'FLAT' &&
        (rules.max_discount_retail_shop_type || 'PERCENTAGE') === 'FLAT' &&
        (rules.max_discount_wholesale_type || 'PERCENTAGE') === 'FLAT';
      setRulesForm({
        minimum_margin_percentage: rules.minimum_margin_percentage?.toString() || '',
        max_discount_end_customer: rules.max_discount_end_customer?.toString() || '',
        max_discount_end_customer_type: rules.max_discount_end_customer_type || 'FLAT',
        max_discount_retail_shop: rules.max_discount_retail_shop?.toString() || '',
        max_discount_retail_shop_type: rules.max_discount_retail_shop_type || 'FLAT',
        max_discount_wholesale: rules.max_discount_wholesale?.toString() || '',
        max_discount_wholesale_type: rules.max_discount_wholesale_type || 'FLAT',
        warning_threshold_percentage: rules.warning_threshold_percentage?.toString() || '0',
        hard_block: rules.hard_block ?? true,
        allow_below_minimum: rules.allow_below_minimum ?? false,
        no_discount: noDiscountDetected,
        reason: ''
      });

      setDiscountSlabs(
        (data.discounts || []).map((slab) => ({
          id: slab.id,
          buyer_type: slab.buyer_type || 'END_CUSTOMER',
          min_quantity_kg: slab.min_quantity_kg?.toString() || '',
          max_quantity_kg: slab.max_quantity_kg?.toString() || '',
          discount_type: slab.discount_type || 'PERCENTAGE',
          discount_value: slab.discount_value?.toString() || '',
          active: slab.active ?? true
        }))
      );
    } catch (error) {
      toast.error('Failed to load pricing module');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleOpenPricingPage = (variant) => {
    navigate(`/products/${variant.id}/pricing`);
  };

  const handleSaveMaster = async () => {
    if (!masterForm.name) {
      toast.error('Please enter a product name');
      return;
    }
    setSaving(true);
    try {
      if (selectedMaster) {
        await adminApi.updateProductMaster(selectedMaster.id, {
          ...masterForm,
          code: masterForm.code || undefined
        });
        toast.success('Product master updated');
      } else {
        await adminApi.createProductMaster({
          ...masterForm,
          code: masterForm.code || undefined
        });
        toast.success('Product master created');
      }
      setShowMasterModal(false);
      resetMasterForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product master');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePack = async () => {
    if (!packForm.label || !packForm.grams) {
      toast.error('Please enter label and grams');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...packForm,
        grams: parseInt(packForm.grams, 10),
        size_code: packForm.size_code || undefined
      };
      if (selectedPack) {
        await adminApi.updatePackSize(selectedPack.id, payload);
        toast.success('Pack size updated');
      } else {
        await adminApi.createPackSize(payload);
        toast.success('Pack size created');
      }
      setShowPackModal(false);
      resetPackForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pack size');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVariant = async () => {
    if (!variantForm.master_id || !variantForm.pack_size_id) {
      toast.error('Select a master product and pack size');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...variantForm,
        sku: variantForm.sku || undefined,
        invoice_name: variantForm.invoice_name,
        reorder_level: variantForm.reorder_level ? parseNumber(variantForm.reorder_level) : undefined
      };

      if (selectedVariant) {
        await adminApi.updateProductVariant(selectedVariant.id, payload);
        toast.success('Variant updated');
      } else {
        await adminApi.createProductVariant(payload);
        toast.success('Variant created');
      }
      setShowVariantModal(false);
      resetVariantForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save variant');
    } finally {
      setSaving(false);
    }
  };

  const handleEditMaster = (master) => {
    setSelectedMaster(master);
    setMasterForm({
      name: master.name,
      description: master.description || '',
      category: master.category || 'spices',
      code: master.code || '',
      hsn_code: master.hsn_code || '',
      is_active: master.is_active
    });
    setShowMasterModal(true);
  };

  const handleEditPack = (pack) => {
    setSelectedPack(pack);
    setPackForm({
      label: pack.label,
      grams: pack.grams.toString(),
      size_code: pack.size_code || '',
      is_active: pack.is_active
    });
    setShowPackModal(true);
  };

  const handleEditVariant = (variant) => {
    setSelectedVariant(variant);
    setVariantForm({
      master_id: variant.master_id,
      pack_size_id: variant.pack_size_id,
      sku: variant.sku || '',
      invoice_name: variant.invoice_name || '',
      is_blended: Boolean(variant.is_blended),
      is_bulk: variant.is_bulk ?? (variant.pack_size_label || '').toLowerCase() === 'bulk',
      reorder_level: variant.reorder_level?.toString() || '',
      is_active: variant.is_active
    });
    setVariantImages(variant.images || []);
    setShowVariantModal(true);
  };

  const syncVariantImages = (updatedVariant) => {
    setVariantImages(updatedVariant.images || []);
    setSelectedVariant((prev) => ({ ...prev, ...updatedVariant, blend_recipe: updatedVariant.blend_recipe ?? prev?.blend_recipe }));
    setVariants((prev) => prev.map((item) => (
      item.id === updatedVariant.id ? { ...item, ...updatedVariant, blend_recipe: updatedVariant.blend_recipe ?? item.blend_recipe } : item
    )));
  };

  const syncVariantMedia = (updatedVariant) => {
    setVariantImages(updatedVariant.images || []);
    setSelectedVariant((prev) => ({ ...prev, ...updatedVariant, blend_recipe: updatedVariant.blend_recipe ?? prev?.blend_recipe }));
    setVariants((prev) => prev.map((item) => (
      item.id === updatedVariant.id ? { ...item, ...updatedVariant, blend_recipe: updatedVariant.blend_recipe ?? item.blend_recipe } : item
    )));
  };

  const handleUploadVariantImages = async (files) => {
    if (!selectedVariant || files.length === 0) return;
    setImageUploading(true);
    try {
      let latestVariant = selectedVariant;
      for (const file of Array.from(files)) {
        const response = await adminApi.uploadVariantImage(latestVariant.id, file);
        latestVariant = response.data;
      }
      syncVariantMedia(latestVariant);
      toast.success('Images uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    if (!selectedVariant) return;
    try {
      const response = await adminApi.setVariantPrimaryImage(selectedVariant.id, imageId);
      syncVariantMedia(response.data);
      toast.success('Primary image updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set primary image');
    }
  };

  const handleDeleteVariantImage = async (imageId) => {
    if (!selectedVariant) return;
    try {
      const response = await adminApi.deleteVariantImage(selectedVariant.id, imageId);
      syncVariantMedia(response.data);
      toast.success('Image deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const handleUploadVariantStickerLabel = async (files) => {
    if (!selectedVariant || !files?.length) return;
    setStickerUploading(true);
    try {
      const response = await adminApi.uploadVariantStickerLabel(selectedVariant.id, files[0]);
      syncVariantMedia(response.data);
      toast.success('Sticker label updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sticker label');
    } finally {
      setStickerUploading(false);
    }
  };

  const handleDeleteVariantStickerLabel = async () => {
    if (!selectedVariant) return;
    try {
      const response = await adminApi.deleteVariantStickerLabel(selectedVariant.id);
      syncVariantMedia(response.data);
      toast.success('Sticker label removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove sticker label');
    }
  };

  const handleSaveCosting = async () => {
    if (!selectedPricingVariant) return;
    if (!costingForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setPricingSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        raw_material_cost_per_kg: parseNumber(costingForm.raw_material_cost_per_kg),
        processing_cost_per_kg: parseNumber(costingForm.processing_cost_per_kg),
        packaging_cost_per_kg: parseNumber(costingForm.packaging_cost_per_kg),
        overhead_cost_per_kg: parseNumber(costingForm.overhead_cost_per_kg),
        wastage_percentage: parseNumber(costingForm.wastage_percentage),
        last_reviewed_on: today,
        reason: costingForm.reason
      };
      await adminApi.updateVariantCosting(selectedPricingVariant.id, payload);
      toast.success('Costing updated');
      setCostingForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule(selectedPricingVariant);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update costing');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleSavePricing = async () => {
    if (!selectedPricingVariant) return;
    if (!pricingForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    const pricingErrors = getPricingErrors();
    if (pricingErrors.length > 0) {
      toast.error(pricingErrors[0]);
      return;
    }
    setPricingSaving(true);
    try {
      const payload = {
        retail_mrp: parseNumber(pricingForm.retail_mrp),
        retail_price: parseNumber(pricingForm.retail_price),
        wholesale_price: parseNumber(pricingForm.wholesale_price),
        pricing_basis: pricingForm.pricing_basis,
        active: pricingForm.active,
        reason: pricingForm.reason
      };
      await adminApi.updateVariantPricing(selectedPricingVariant.id, payload);
      toast.success('Pricing updated');
      setPricingForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule(selectedPricingVariant);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleSaveRules = async () => {
    if (!selectedPricingVariant) return;
    if (!rulesForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setPricingSaving(true);
    try {
      const noDiscount = rulesForm.no_discount;
      const payload = {
        minimum_margin_percentage: parseNumber(rulesForm.minimum_margin_percentage),
        max_discount_end_customer: noDiscount ? 0 : parseNumber(rulesForm.max_discount_end_customer),
        max_discount_end_customer_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_end_customer_type || 'FLAT'),
        max_discount_retail_shop: noDiscount ? 0 : parseNumber(rulesForm.max_discount_retail_shop),
        max_discount_retail_shop_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_retail_shop_type || 'FLAT'),
        max_discount_wholesale: noDiscount ? 0 : parseNumber(rulesForm.max_discount_wholesale),
        max_discount_wholesale_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_wholesale_type || 'FLAT'),
        warning_threshold_percentage: parseNumber(rulesForm.warning_threshold_percentage || '0'),
        hard_block: rulesForm.hard_block,
        allow_below_minimum: rulesForm.allow_below_minimum,
        reason: rulesForm.reason
      };
      await adminApi.updateVariantPriceRules(selectedPricingVariant.id, payload);
      toast.success('Pricing rules updated');
      setRulesForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule(selectedPricingVariant);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rules');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleSaveDiscounts = async () => {
    if (!selectedPricingVariant) return;
    if (rulesForm.no_discount) {
      toast.error('Disable "No Discount" to edit discount slabs');
      return;
    }
    if (!discountReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setPricingSaving(true);
    try {
      const payload = {
        slabs: discountSlabs.map((slab) => ({
          buyer_type: slab.buyer_type,
          min_quantity_kg: parseNumber(slab.min_quantity_kg),
          max_quantity_kg: parseNumber(slab.max_quantity_kg),
          discount_type: slab.discount_type,
          discount_value: parseNumber(slab.discount_value),
          active: slab.active
        })),
        reason: discountReason
      };
      await adminApi.updateVariantDiscountSlabs(selectedPricingVariant.id, payload);
      toast.success('Discount slabs updated');
      setDiscountReason('');
      await loadPricingModule(selectedPricingVariant);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update discounts');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleAddDiscountSlab = () => {
    setDiscountSlabs((prev) => ([
      ...prev,
      {
        buyer_type: 'END_CUSTOMER',
        min_quantity_kg: '',
        max_quantity_kg: '',
        discount_type: 'FLAT',
        discount_value: '',
        active: true
      }
    ]));
  };

  const handleRemoveDiscountSlab = (index) => {
    setDiscountSlabs((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleToggleNoDiscount = (checked) => {
    setRulesForm((prev) => ({
      ...prev,
      no_discount: checked,
      max_discount_end_customer: checked ? '0' : prev.max_discount_end_customer,
      max_discount_retail_shop: checked ? '0' : prev.max_discount_retail_shop,
      max_discount_wholesale: checked ? '0' : prev.max_discount_wholesale,
      max_discount_end_customer_type: 'FLAT',
      max_discount_retail_shop_type: 'FLAT',
      max_discount_wholesale_type: 'FLAT'
    }));
    if (checked) {
      setDiscountSlabs([]);
    } else {
      setDiscountSlabs((prev) => prev.map((slab) => ({
        ...slab,
        discount_type: slab.discount_type || 'FLAT'
      })));
    }
  };

  const costingTotals = computeCostingTotals();
  const minSelling = computeMinimumSellingPrice();
  const pricingErrors = getPricingErrors();
  const pricingReady = Boolean(pricingData.costing && pricingData.rules);
  const rulesReady = Boolean(pricingData.costing);
  const discountsReady = Boolean(pricingData.pricing && pricingData.rules);
  const mspPerPack = minSelling.minPricePerPack || 0;
  const maxDiscountEffective = (basePrice, maxDiscount, discountType) => {
    const base = parseNumber(basePrice);
    const discount = parseNumber(maxDiscount);
    if (!base) return 0;
    if (discountType === 'FLAT') return base - discount;
    return base * (1 - discount / 100);
  };
  const priceStatus = (effectivePrice) => {
    if (!mspPerPack) return { color: 'text-[#A8A8A8]', warn: false };
    return effectivePrice < mspPerPack
      ? { color: 'text-red-600', warn: true }
      : { color: 'text-green-600', warn: false };
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Products</h1>
        <p className="text-[#A8A8A8] mt-1">Manage masters, pack sizes, and variants</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[#E5E5E5]">
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="masters">Product Masters</TabsTrigger>
          <TabsTrigger value="packs">Pack Sizes</TabsTrigger>
        </TabsList>

        <TabsContent value="variants">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Product Variants</h2>
              <p className="text-sm text-[#A8A8A8]">Combination of master product + pack size</p>
            </div>
            <Button
              onClick={() => { resetVariantForm(); setShowVariantModal(true); }}
              className="bg-[#C97A5E] hover:bg-[#B06850] text-white"
              data-testid="add-variant-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Variant
            </Button>
          </div>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
                </div>
              ) : variants.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
                  <Package className="w-12 h-12 mb-4" />
                  <p>No variants found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 border-b border-[#E5E5E5] bg-[#FAFAF9]">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Fast moving</p>
                      <p className="text-2xl font-bold text-emerald-800">{variantHealthStats.fast}</p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Slow moving</p>
                      <p className="text-2xl font-bold text-amber-800">{variantHealthStats.slow}</p>
                    </div>
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Dead stock risk</p>
                      <p className="text-2xl font-bold text-rose-800">{variantHealthStats.dead}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-700 font-semibold">Normal</p>
                      <p className="text-2xl font-bold text-slate-800">{variantHealthStats.normal}</p>
                    </div>
                  </div>
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">SKU</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Product</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Pack Size</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Stock</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Inventory Health</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {variants.map((variant) => (
                        <tr key={variant.id} className="hover:bg-[#FAFAF9]/50 transition-colors">
                          <td className="py-4 px-6 text-[#2D2420] font-medium">{variant.sku}</td>
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-[#2D2420]">{variant.master_name}</p>
                              <p className="text-xs text-[#A8A8A8]">{variant.master_code}</p>
                              <p className="text-xs text-[#C97A5E]">Printed as: {variant.invoice_name || `${variant.master_name} ${variant.pack_size_label}`.trim()}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-[#2D2420]">{variant.pack_size_label}</p>
                              <p className="text-xs text-[#A8A8A8]">{variant.pack_size_grams}g</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            {(() => {
                              const stock = typeof variant.stock_available === 'number'
                                ? variant.stock_available
                                : null;
                              const reorderLevel = typeof variant.reorder_level === 'number'
                                ? variant.reorder_level
                                : 0;
                              if (stock === null) {
                                return <span className="text-[#A8A8A8]">—</span>;
                              }
                              const color = stock < reorderLevel ? 'text-red-600' : 'text-green-600';
                              return <span className={`font-semibold ${color}`}>{Math.round(stock)}</span>;
                            })()}
                          </td>
                          <td className="py-4 px-6">
                            {(() => {
                              const healthMeta = getInventoryHealthMeta(variant.inventory_health);
                              return (
                                <div>
                                  <Badge className={cn('text-xs', healthMeta.className)}>
                                    {healthMeta.label}
                                  </Badge>
                                  <p className="text-[11px] text-[#A8A8A8] mt-1">
                                    30d: {variant.order_frequency_30d ?? 0} orders
                                  </p>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-4 px-6">
                            <Badge className={cn(
                              "text-xs",
                              variant.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            )}>
                              {variant.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenPricingPage(variant)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                title="Pricing & Costing"
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditVariant(variant)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="masters">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Product Masters</h2>
              <p className="text-sm text-[#A8A8A8]">Base product definitions</p>
            </div>
            <Button
              onClick={() => { resetMasterForm(); setShowMasterModal(true); }}
              className="bg-[#C97A5E] hover:bg-[#B06850] text-white"
              data-testid="add-master-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Master
            </Button>
          </div>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
                </div>
              ) : masters.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
                  <Package className="w-12 h-12 mb-4" />
                  <p>No product masters found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Name</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Code</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">HSN</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Category</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {masters.map((master) => (
                        <tr key={master.id} className="hover:bg-[#FAFAF9]/50 transition-colors">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-[#2D2420]">{master.name}</p>
                              <p className="text-xs text-[#A8A8A8] line-clamp-1">{master.description || '-'}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-[#2D2420]">{master.code}</td>
                          <td className="py-4 px-6 text-[#2D2420]">{master.hsn_code || '-'}</td>
                          <td className="py-4 px-6">
                            <Badge className="bg-[#D2B48C]/20 text-[#5C4033]">{master.category}</Badge>
                          </td>
                          <td className="py-4 px-6">
                            <Badge className={cn(
                              "text-xs",
                              master.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            )}>
                              {master.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditMaster(master)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packs">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Pack Sizes</h2>
              <p className="text-sm text-[#A8A8A8]">Standard packaging options</p>
            </div>
            <Button
              onClick={() => { resetPackForm(); setShowPackModal(true); }}
              className="bg-[#C97A5E] hover:bg-[#B06850] text-white"
              data-testid="add-pack-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Pack Size
            </Button>
          </div>

          <Card className="bg-white border-[#E5E5E5]">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
                </div>
              ) : packSizes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
                  <Package className="w-12 h-12 mb-4" />
                  <p>No pack sizes found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Label</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Grams</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Code</th>
                        <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {packSizes.map((pack) => (
                        <tr key={pack.id} className="hover:bg-[#FAFAF9]/50 transition-colors">
                          <td className="py-4 px-6 text-[#2D2420] font-medium">{pack.label}</td>
                          <td className="py-4 px-6 text-[#2D2420]">{pack.grams}g</td>
                          <td className="py-4 px-6 text-[#2D2420]">{pack.size_code}</td>
                          <td className="py-4 px-6">
                            <Badge className={cn(
                              "text-xs",
                              pack.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            )}>
                              {pack.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditPack(pack)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pricing & Costing Modal */}
      <Dialog
        open={showPricingModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowPricingModal(false);
            setSelectedPricingVariant(null);
            resetPricingForms();
          }
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              Pricing & Costing — {selectedPricingVariant?.master_name || 'Product'}
              {selectedPricingVariant ? ` (${selectedPricingVariant.pack_size_label})` : ''}
            </DialogTitle>
          </DialogHeader>

          {pricingLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : (
            <TooltipProvider>
              <Tabs value={pricingTab} onValueChange={setPricingTab}>
              <TabsList className="bg-white border border-[#E5E5E5]">
                <TabsTrigger value="costing">Costing</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="rules">Non-Negotiable Rules</TabsTrigger>
                <TabsTrigger value="discounts">Discounts</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="costing" className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Raw Material Cost / Kg"
                      tooltip="Base raw material cost per kg before processing and packaging."
                    />
                    <Input
                      type="number"
                      value={costingForm.raw_material_cost_per_kg}
                      onChange={(e) => setCostingForm({ ...costingForm, raw_material_cost_per_kg: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Processing Cost / Kg"
                      tooltip="Manufacturing or processing cost allocated per kg."
                    />
                    <Input
                      type="number"
                      value={costingForm.processing_cost_per_kg}
                      onChange={(e) => setCostingForm({ ...costingForm, processing_cost_per_kg: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Packaging Cost / Kg"
                      tooltip="Packaging materials and packing labor per kg."
                    />
                    <Input
                      type="number"
                      value={costingForm.packaging_cost_per_kg}
                      onChange={(e) => setCostingForm({ ...costingForm, packaging_cost_per_kg: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Overhead Cost / Kg"
                      tooltip="Allocated overheads per kg (utilities, admin, rent, etc.)."
                    />
                    <Input
                      type="number"
                      value={costingForm.overhead_cost_per_kg}
                      onChange={(e) => setCostingForm({ ...costingForm, overhead_cost_per_kg: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Wastage %"
                      tooltip="Applied on subtotal to cover loss during processing/packing."
                    />
                    <Input
                      type="number"
                      value={costingForm.wastage_percentage}
                      onChange={(e) => setCostingForm({ ...costingForm, wastage_percentage: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                </div>

                <Card className="bg-white border-[#E5E5E5]">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[#A8A8A8]">Total Cost / Kg</p>
                        <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(costingTotals.totalCostPerKg || 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#A8A8A8]">Total Cost / Pack</p>
                        <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(costingTotals.totalCostPerPack || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  <LabelWithTooltip
                    text="Change Reason *"
                    tooltip="Required for audit trail. This reason will be stored in history."
                  />
                  <Input
                    value={costingForm.reason}
                    onChange={(e) => setCostingForm({ ...costingForm, reason: e.target.value })}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveCosting}
                    disabled={pricingSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {pricingSaving ? 'Saving...' : 'Save Costing'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-6 mt-4">
                {!pricingReady && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    Set costing and non-negotiable rules before saving pricing.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Retail MRP"
                      tooltip="Price for direct end customers."
                    />
                    <Input
                      type="number"
                      value={pricingForm.retail_mrp}
                      onChange={(e) => setPricingForm({ ...pricingForm, retail_mrp: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = parseNumber(pricingForm.retail_mrp);
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Retail Price"
                      tooltip="Price for retail shops."
                    />
                    <Input
                      type="number"
                      value={pricingForm.retail_price}
                      onChange={(e) => setPricingForm({ ...pricingForm, retail_price: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = parseNumber(pricingForm.retail_price);
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Wholesale Price"
                      tooltip="Price for wholesale customers."
                    />
                    <Input
                      type="number"
                      value={pricingForm.wholesale_price}
                      onChange={(e) => setPricingForm({ ...pricingForm, wholesale_price: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = parseNumber(pricingForm.wholesale_price);
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Pricing Basis"
                      tooltip="Choose whether pricing is cost-based or market-based."
                    />
                    <Select
                      value={pricingForm.pricing_basis}
                      onValueChange={(value) => setPricingForm({ ...pricingForm, pricing_basis: value })}
                    >
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select basis" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="COST_BASED">Cost Based</SelectItem>
                        <SelectItem value="MARKET_BASED">Market Based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Card className="bg-white border-[#E5E5E5]">
                  <CardContent className="p-4">
                    <p className="text-sm text-[#A8A8A8] mb-2">Margin Impact (per pack)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {[
                        { label: 'End Customer (MRP)', value: pricingForm.retail_mrp },
                        { label: 'Retail Shop', value: pricingForm.retail_price },
                        { label: 'Wholesale', value: pricingForm.wholesale_price }
                      ].map((item) => {
                        const priceValue = parseNumber(item.value);
                        const margin = costingTotals.totalCostPerPack
                          ? ((priceValue - costingTotals.totalCostPerPack) / costingTotals.totalCostPerPack) * 100
                          : 0;
                        return (
                          <div key={item.label} className="flex items-center justify-between">
                            <span className="text-[#5C4033]">{item.label}</span>
                            <span className="font-semibold text-[#2D2420]">
                              {Number.isFinite(margin) ? `${margin.toFixed(1)}%` : '—'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {pricingErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    {pricingErrors[0]}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#5C4033]">
                    <input
                      type="checkbox"
                      checked={pricingForm.active}
                      onChange={(e) => setPricingForm({ ...pricingForm, active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>

                <div className="space-y-2">
                  <LabelWithTooltip
                    text="Change Reason *"
                    tooltip="Required for audit trail. This reason will be stored in history."
                  />
                  <Input
                    value={pricingForm.reason}
                    onChange={(e) => setPricingForm({ ...pricingForm, reason: e.target.value })}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSavePricing}
                    disabled={pricingSaving || pricingErrors.length > 0 || !pricingReady}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {pricingSaving ? 'Saving...' : 'Save Pricing'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="rules" className="space-y-6 mt-4">
                {!rulesReady && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    Set costing before saving non-negotiable rules.
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-[#5C4033]">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rulesForm.no_discount}
                      onChange={(e) => handleToggleNoDiscount(e.target.checked)}
                    />
                    No Discount
                  </label>
                  {rulesForm.no_discount && (
                    <span className="text-xs text-[#A8A8A8]">All discounts set to flat 0.</span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Minimum Margin %"
                      tooltip="MSP is derived as total cost per pack plus this margin percentage."
                    />
                    <Input
                      type="number"
                      value={rulesForm.minimum_margin_percentage}
                      onChange={(e) => setRulesForm({ ...rulesForm, minimum_margin_percentage: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Warning Threshold %"
                      tooltip="Show warning when margin falls below this percentage."
                    />
                    <Input
                      type="number"
                      value={rulesForm.warning_threshold_percentage}
                      onChange={(e) => setRulesForm({ ...rulesForm, warning_threshold_percentage: e.target.value })}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                </div>

                <Card className="bg-[#FFF7ED] border border-[#F5D0A9]">
                  <CardContent className="p-4 space-y-2">
                    <p className="text-sm text-[#A15C1B]">Minimum Selling Price (auto)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-[#A15C1B]">Per Kg</p>
                        <p className="text-lg font-semibold text-[#5C4033]">{formatCurrency(minSelling.minPricePerKg || 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A15C1B]">Per Pack</p>
                        <p className="text-lg font-semibold text-[#5C4033]">{formatCurrency(minSelling.minPricePerPack || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Max Discount % (End Customer)"
                      tooltip="Maximum discount allowed on Retail MRP. Effective price shown below."
                    />
                    <Input
                      type="number"
                      value={rulesForm.max_discount_end_customer}
                      onChange={(e) => setRulesForm({ ...rulesForm, max_discount_end_customer: e.target.value })}
                      disabled={rulesForm.no_discount}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = maxDiscountEffective(
                        pricingForm.retail_mrp,
                        rulesForm.max_discount_end_customer,
                        rulesForm.max_discount_end_customer_type
                      );
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Max Discount % (Retail Shop)"
                      tooltip="Maximum discount allowed on Retail price. Effective price shown below."
                    />
                    <Input
                      type="number"
                      value={rulesForm.max_discount_retail_shop}
                      onChange={(e) => setRulesForm({ ...rulesForm, max_discount_retail_shop: e.target.value })}
                      disabled={rulesForm.no_discount}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = maxDiscountEffective(
                        pricingForm.retail_price,
                        rulesForm.max_discount_retail_shop,
                        rulesForm.max_discount_retail_shop_type
                      );
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <LabelWithTooltip
                      text="Max Discount % (Wholesale)"
                      tooltip="Maximum discount allowed on Wholesale price. Effective price shown below."
                    />
                    <Input
                      type="number"
                      value={rulesForm.max_discount_wholesale}
                      onChange={(e) => setRulesForm({ ...rulesForm, max_discount_wholesale: e.target.value })}
                      disabled={rulesForm.no_discount}
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                    {(() => {
                      const effective = maxDiscountEffective(
                        pricingForm.wholesale_price,
                        rulesForm.max_discount_wholesale,
                        rulesForm.max_discount_wholesale_type
                      );
                      const status = priceStatus(effective);
                      return (
                        <p className={`text-xs ${status.color}`}>
                          {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                        </p>
                      );
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm text-[#5C4033]">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rulesForm.hard_block}
                      onChange={(e) => setRulesForm({ ...rulesForm, hard_block: e.target.checked })}
                    />
                    <span className="flex items-center gap-2">
                      Hard Block Below MSP
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-[#A8A8A8] hover:text-[#5C4033] transition-colors"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Blocks saving if any price is below MSP.</TooltipContent>
                      </Tooltip>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-[#A8A8A8]">
                    <input type="checkbox" checked={rulesForm.allow_below_minimum} disabled />
                    Allow Below Minimum (disabled)
                  </label>
                </div>

                <div className="space-y-2">
                  <LabelWithTooltip
                    text="Change Reason *"
                    tooltip="Required for audit trail. This reason will be stored in history."
                  />
                  <Input
                    value={rulesForm.reason}
                    onChange={(e) => setRulesForm({ ...rulesForm, reason: e.target.value })}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveRules}
                    disabled={pricingSaving || !rulesReady}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {pricingSaving ? 'Saving...' : 'Save Rules'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="discounts" className="space-y-6 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-[#2D2420]">Discount Slabs</h3>
                    <p className="text-sm text-[#A8A8A8]">Quantity based discounts by buyer type</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleAddDiscountSlab}
                    disabled={!discountsReady || rulesForm.no_discount}
                    className="border-[#E5E5E5]"
                  >
                    Add Slab
                  </Button>
                </div>

                {!discountsReady && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    Set pricing and rules before adding discounts.
                  </div>
                )}
                {rulesForm.no_discount && (
                  <div className="bg-[#FFF7ED] border border-[#F5D0A9] rounded-lg p-3 text-sm text-[#A15C1B]">
                    Discounts are disabled because "No Discount" is enabled in non-negotiable rules.
                  </div>
                )}

                <div className="space-y-3">
                  {discountSlabs.length === 0 ? (
                    <p className="text-sm text-[#A8A8A8]">No discount slabs yet.</p>
                  ) : (
                    discountSlabs.map((slab, index) => (
                      <Card key={index} className="bg-white border-[#E5E5E5]">
                        <CardContent className="p-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <LabelWithTooltip
                                text="Buyer Type"
                                tooltip="Select the buyer category this slab applies to."
                              />
                              <Select
                                value={slab.buyer_type}
                                onValueChange={(value) => {
                                  const updated = [...discountSlabs];
                                  updated[index].buyer_type = value;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                              >
                                <SelectTrigger className="border-[#E5E5E5]">
                                  <SelectValue placeholder="Select buyer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {buyerTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <LabelWithTooltip
                                text="Discount Type"
                                tooltip="Percentage of base price or flat amount per pack."
                              />
                              <Select
                                value={slab.discount_type}
                                onValueChange={(value) => {
                                  const updated = [...discountSlabs];
                                  updated[index].discount_type = value;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                              >
                                <SelectTrigger className="border-[#E5E5E5]">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                                  <SelectItem value="FLAT">Flat</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <LabelWithTooltip
                                text="Min Quantity (kg)"
                                tooltip="Starting quantity (in kg) for this discount slab."
                              />
                              <Input
                                type="number"
                                value={slab.min_quantity_kg}
                                onChange={(e) => {
                                  const updated = [...discountSlabs];
                                  updated[index].min_quantity_kg = e.target.value;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                              />
                            </div>
                            <div className="space-y-2">
                              <LabelWithTooltip
                                text="Max Quantity (kg)"
                                tooltip="Ending quantity (in kg) for this discount slab."
                              />
                              <Input
                                type="number"
                                value={slab.max_quantity_kg}
                                onChange={(e) => {
                                  const updated = [...discountSlabs];
                                  updated[index].max_quantity_kg = e.target.value;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                              />
                            </div>
                            <div className="space-y-2">
                              <LabelWithTooltip
                                text="Discount Value"
                                tooltip="Discount amount. Interpreted as percentage or flat value based on type."
                              />
                              <Input
                                type="number"
                                value={slab.discount_value}
                                onChange={(e) => {
                                  const updated = [...discountSlabs];
                                  updated[index].discount_value = e.target.value;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                              />
                            </div>
                            <div className="flex items-center gap-2 text-sm text-[#5C4033]">
                              <input
                                type="checkbox"
                                checked={slab.active}
                                onChange={(e) => {
                                  const updated = [...discountSlabs];
                                  updated[index].active = e.target.checked;
                                  setDiscountSlabs(updated);
                                }}
                                disabled={rulesForm.no_discount}
                              />
                              Active
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              onClick={() => handleRemoveDiscountSlab(index)}
                              disabled={rulesForm.no_discount}
                              className="border-[#E5E5E5]"
                            >
                              Remove
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <div className="space-y-2">
                  <LabelWithTooltip
                    text="Change Reason *"
                    tooltip="Required for audit trail. This reason will be stored in history."
                  />
                  <Input
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveDiscounts}
                    disabled={pricingSaving || !discountsReady || rulesForm.no_discount}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {pricingSaving ? 'Saving...' : 'Save Discounts'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="history" className="space-y-4 mt-4">
                {pricingData.history.length === 0 ? (
                  <p className="text-sm text-[#A8A8A8]">No history yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Changed By</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {pricingData.history.map((entry) => (
                          <tr key={entry.id}>
                            <td className="py-3 px-4 text-sm text-[#2D2420]">
                              {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.entity_type}</td>
                            <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.changed_by?.name || '-'}</td>
                            <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
              </Tabs>
            </TooltipProvider>
          )}
        </DialogContent>
      </Dialog>

      {/* Master Modal */}
      <Dialog open={showMasterModal} onOpenChange={(open) => { if (!open) resetMasterForm(); setShowMasterModal(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {selectedMaster ? 'Edit Product Master' : 'Add Product Master'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name *</Label>
              <Input
                value={masterForm.name}
                onChange={(e) => setMasterForm({ ...masterForm, name: e.target.value })}
                placeholder="Enter product name"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Description</Label>
              <Textarea
                value={masterForm.description}
                onChange={(e) => setMasterForm({ ...masterForm, description: e.target.value })}
                placeholder="Enter description"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Category</Label>
              <Select
                value={masterForm.category}
                onValueChange={(value) => setMasterForm({ ...masterForm, category: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spices">Spices</SelectItem>
                  <SelectItem value="masala">Masala</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Code (optional)</Label>
              <Input
                value={masterForm.code}
                onChange={(e) => setMasterForm({ ...masterForm, code: e.target.value })}
                placeholder="Auto-generated if empty"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">HSN Code</Label>
              <Input
                value={masterForm.hsn_code}
                onChange={(e) => setMasterForm({ ...masterForm, hsn_code: e.target.value })}
                placeholder="e.g. 0910"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
              <p className="text-xs text-[#A8A8A8]">Used on GST invoices for all variants under this product master.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetMasterForm(); setShowMasterModal(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMaster}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pack Modal */}
      <Dialog open={showPackModal} onOpenChange={(open) => { if (!open) resetPackForm(); setShowPackModal(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {selectedPack ? 'Edit Pack Size' : 'Add Pack Size'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Label *</Label>
              <Input
                value={packForm.label}
                onChange={(e) => setPackForm({ ...packForm, label: e.target.value })}
                placeholder="e.g. 50g"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Grams *</Label>
              <Input
                type="number"
                value={packForm.grams}
                onChange={(e) => setPackForm({ ...packForm, grams: e.target.value })}
                placeholder="e.g. 50"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Size Code (optional)</Label>
              <Input
                value={packForm.size_code}
                onChange={(e) => setPackForm({ ...packForm, size_code: e.target.value })}
                placeholder="Auto-generated if empty"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetPackForm(); setShowPackModal(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePack}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variant Modal */}
      <Dialog open={showVariantModal} onOpenChange={(open) => { if (!open) resetVariantForm(); setShowVariantModal(open); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {selectedVariant ? 'Edit Variant' : 'Add Variant'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto pr-1 flex-1">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Product Master *</Label>
              <Select
                value={variantForm.master_id}
                onValueChange={(value) => setVariantForm({ ...variantForm, master_id: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select master" />
                </SelectTrigger>
                <SelectContent>
                  {masterOptions.map((master) => (
                    <SelectItem key={master.id} value={master.id}>{master.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Pack Size *</Label>
              <Select
                value={variantForm.pack_size_id}
                onValueChange={(value) => {
                  const selectedPack = packOptions.find((p) => p.id === value);
                  const isBulkPack = (selectedPack?.label || '').toLowerCase() === 'bulk';
                  setVariantForm({ ...variantForm, pack_size_id: value, is_bulk: isBulkPack || variantForm.is_bulk });
                }}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select pack size" />
                </SelectTrigger>
                <SelectContent>
                  {packOptions.map((pack) => (
                    <SelectItem key={pack.id} value={pack.id}>{pack.label} ({pack.grams}g)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">SKU (optional)</Label>
              <Input
                value={variantForm.sku}
                onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })}
                placeholder="Auto-generated if empty"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Invoice / Printed Name</Label>
              <Input
                value={variantForm.invoice_name}
                onChange={(e) => setVariantForm({ ...variantForm, invoice_name: e.target.value })}
                placeholder={invoiceNamePreview || 'Auto-generated printed name'}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
              <p className="text-xs text-[#A8A8A8]">
                This name is used on customer-facing invoices and BOS documents. Leave empty to use the default printed name.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Reorder Level</Label>
              <Input
                type="number"
                value={variantForm.reorder_level}
                onChange={(e) => setVariantForm({ ...variantForm, reorder_level: e.target.value })}
                placeholder="Enter reorder level"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-[#5C4033]">
                <input
                  type="checkbox"
                  checked={variantForm.is_bulk}
                  onChange={(e) => setVariantForm({ ...variantForm, is_bulk: e.target.checked })}
                />
                Mark as Bulk (invoice prints as 1kg)
              </label>
              {invoiceNamePreview && (
                <p className="text-xs text-[#A8A8A8]">
                  Default printed name: {invoiceNamePreview}
                </p>
              )}
            </div>
            {selectedVariant ? (
              <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
                <Label className="text-[#5C4033]">Variant Images</Label>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleUploadVariantImages(e.target.files)}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  disabled={imageUploading}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {variantImages.map((image) => (
                    <div key={image.id} className="border border-[#E5E5E5] rounded-lg p-2 space-y-2">
                      <div className="relative">
                        <img
                          src={getVariantImageSrc(image)}
                          alt="Variant"
                          className="w-full h-24 object-cover rounded"
                        />
                        {image.is_primary && (
                          <span className="absolute top-2 left-2 text-xs bg-[#C97A5E] text-white px-2 py-0.5 rounded">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetPrimaryImage(image.id)}
                          disabled={image.is_primary}
                          className="border-[#E5E5E5] text-xs"
                        >
                          Set Primary
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteVariantImage(image.id)}
                          className="text-red-600 hover:bg-red-50 text-xs"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {variantImages.length === 0 && (
                  <p className="text-xs text-[#A8A8A8]">No images uploaded yet.</p>
                )}
                <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
                  <Label className="text-[#5C4033]">Sticker Label</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUploadVariantStickerLabel(e.target.files)}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    disabled={stickerUploading}
                  />
                  {selectedVariant?.sticker_label_url ? (
                    <div className="border border-[#E5E5E5] rounded-lg p-3 space-y-3">
                      <img
                        src={selectedVariant.sticker_label_url}
                        alt="Sticker label"
                        className="w-full max-h-64 object-contain rounded bg-[#FAFAF9]"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <a
                          href={selectedVariant.sticker_label_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-[#C97A5E] hover:underline"
                        >
                          Preview Full Size
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteVariantStickerLabel}
                          className="text-red-600 hover:bg-red-50 text-xs"
                        >
                          Remove Label
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-[#A8A8A8]">No sticker label uploaded yet.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-[#A8A8A8]">Save the variant to upload images.</p>
            )}
          </div>
          <DialogFooter className="border-t border-[#E5E5E5] pt-4 mt-2 bg-white">
            <Button variant="outline" onClick={() => { resetVariantForm(); setShowVariantModal(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveVariant}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
